import React, {FormEvent, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Box, Button, Divider, IconButton, Stack, Typography} from '@mui/material';
import {useSnackbar} from 'notistack';
import {finalize} from 'rxjs';

import {ExecutionComment, executionsService} from '@store/operations/executions';
import {sessionQuery} from '@store/session';

import {AddWhite} from '@icons/index';
import {Colors} from '@constants/colors.constants';

import TextFieldComponent from '@components/inputs/textField/TextField.component';

interface CommentsFormProps {
  executionId: number;
  readOnly?: boolean;
}

const CommentsForm = (props: CommentsFormProps) => {
  const {executionId, readOnly} = props;

  const {t} = useTranslation();
  const {enqueueSnackbar} = useSnackbar();

  const [loading, setLoading] = useState(false);

  const [addComment, setAddComment] = useState<string | false>(false);

  const [comments, setComments] = useState<ExecutionComment[]>([]);

  useEffect(() => {
    if (executionId) {
      executionsService.getExecutionComments(executionId).subscribe({
        next: setComments,
        error: (err) => enqueueSnackbar(err.text, err.options),
      });
    }
  }, [enqueueSnackbar, executionId]);

  const handleSubmitComment = (evt: FormEvent) => {
    evt.preventDefault();

    if (!!addComment && !!executionId) {
      setLoading(true);
      executionsService.addExecutionComment(executionId, addComment).pipe(finalize(() => setLoading(false))).subscribe({
        next: (comment) => {
          enqueueSnackbar(t('operations.executions.success.comment'), {variant: 'success'});
          setComments(comments.concat(comment));
          setAddComment(false);
        },
        error: (err) => enqueueSnackbar(err.text, err.options),
      });
    }
  };

  if (!executionId) return null;

  return (
    <>
      <Stack py="20px" spacing={2}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography fontSize={16} fontWeight="700">
            {t('operations.executions.manage.comments')}
          </Typography>
          {!readOnly && addComment === false && (
            <IconButton aria-label="small-square-text" centerRipple={false} onClick={() => setAddComment('')}>
              <AddWhite/>
            </IconButton>
          )}
        </Stack>
        <Stack spacing={1.5}>
          {comments.map((comment) => {
            const isAuthor = !comment.author || comment.author.id === sessionQuery.currentUser?.userId;
            return (
              <Stack key={comment.id} spacing={0.5} alignSelf={isAuthor ? 'flex-end' : undefined} maxWidth="80%">
                <Stack direction="row" spacing={0.75} alignSelf={isAuthor ? 'flex-end' : undefined}>
                  <Typography fontSize={14} fontWeight="400">
                    {isAuthor ? t('global.you') : `${comment.author?.firstName} ${comment.author?.lastName}`}:
                  </Typography>
                  <Typography fontSize={14} fontWeight="400" color={Colors.textWithOpacity}>
                    {comment.createdAt ? t('global.fullDateTime', {date: new Date(comment.createdAt)}) : '-'}
                  </Typography>
                </Stack>
                <Box
                  py="9px" px="14px"
                  bgcolor={Colors.text}
                  borderRadius={`4px 4px ${isAuthor ? 0 : 4}px ${!isAuthor ? 0 : 4}px`}>
                  <Typography fontSize={16} fontWeight="400" color={Colors.white}>
                    {comment.content}
                  </Typography>
                </Box>
              </Stack>
            );
          })}
        </Stack>
        {addComment !== false && (
          <>
            <Stack width="80%" alignSelf="flex-end" pt="20px">
              <TextFieldComponent
                color="secondary"
                multiline
                minRows={2}
                value={addComment}
                handleChange={setAddComment}/>
            </Stack>
            <Stack direction="row" spacing={1} alignSelf="flex-end">
              <Button variant="outlined" disabled={loading} onClick={() => setAddComment(false)}>
                {t('global.cancel')}
              </Button>
              <Button disabled={loading || !addComment || !addComment.trim()} onClick={handleSubmitComment}>
                {t('global.send')}
              </Button>
            </Stack>
          </>
        )}
      </Stack>
      <Divider variant="fullWidth" orientation="horizontal"/>
    </>
  );
};

export default CommentsForm;
